import {
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

import SimpleCombobox from "../../components/SimpleCombobox";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import { debounce } from "lodash";

export const NewSurgeryForm = (props) => {
    const [newSurgery, setNewSurgery] = useState({
        patient: null,
        date: "",
        surgeon: null,
        assistant: null,
        anesthesiologist: null,
    });
    const [medicals, setMedicals] = useState([]);
    const [invoice, setInvoice] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [patients, setPatients] = useState([]);
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const invoiceRef = useRef(null);
    const [formErrors, setFormErrors] = useState({
        patient: false,
        date: false,
        surgeon: false,
        assistant: false,
        anesthesiologist: false,
        invoiceNumber: false,
    });
    const closeDialog = () => {
        props.dialogRef.current?.close();
    };

    const handleChange = (field, value) => {
        setNewSurgery((prev) => ({
            ...prev,
            [field]:
                typeof value === "object" && value !== null ? value.id : value,
        }));
        setFormErrors({ ...formErrors, [field]: false });
    };

    const handleQueryChange = useCallback(
        debounce(async (text) => {
            api.patients
                .getMulti("firstName", text, null, null, null)
                .then((response) => {
                    const { results } = response;
                    const patientsOptions = results.map((person) => ({
                        ...person,
                        name: `${person.firstName} ${person.lastName} ${person.govId}`,
                    }));
                    setPatients(patientsOptions);
                });
        }, 300),
        []
    );

    useEffect(() => {
        api.patients.getMulti("firstName", null, null, null, null).then((response) => {
            const { results } = response;
            const patientsOptions = results.map((person) => ({
                ...person,
                name: person.firstName,
            }));
            setPatients(patientsOptions);
        });
    }, []);

    useEffect(() => {
        api.medicals.getMulti().then((response) => {
            const data = response;
            setMedicals(data);
        });
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            await api.surgerySchedules.create(newSurgery);
            const updatedSchedules = await api.surgerySchedules.getMulti(
                null,
                null,
                null,
                null,
                {
                    status: 1,
                }
            );
            props.setSurgerySchedules(updatedSchedules);
            invoiceRef.current?.close();
            props.dialogRef.current?.close();
            setNewSurgery({
                patient: null,
                date: "",
                surgeon: null,
                assistant: null,
                anesthesiologist: null,
            });
            setInvoice(null);
        } catch (error) {
            console.error("Error al agendar la cirugía:", error);
            alert("Hubo un error al guardar la cirugía. Inténtalo nuevamente.");
        }
    };

    const checkInvoice = (e) => {
        e.preventDefault();

        const newErrors = {
            date: newSurgery.date.trim() === "" && "Este campo es obligatorio",
            patient: !newSurgery.patient && "Este campo es obligatorio",
            surgeon: !newSurgery.surgeon && "Este campo es obligatorio",
            // assistant: !newSurgery.assistant && "Este campo es obligatorio",
            // anesthesiologist:
            //     !newSurgery.anesthesiologist && "Este campo es obligatorio",
            invoiceNumber:
                invoiceNumber.trim() === "" && "Este campo es obligatorio",
        };
        setFormErrors(newErrors);
        const hasErrors = Object.values(newErrors).some((e) => e);
        if (hasErrors) return;

        setInvoiceLoading(true);
        api.invoices
            .getMulti(null, invoiceNumber, null, null, null)
            .then((response) => {
                const data = response;
                setInvoice(data[0]);
                if (data[0]) {
                    setNewSurgery((prev) => ({
                        ...prev,
                        invoice: data[0].id,
                    }));
                }
            })
            .finally(() => setInvoiceLoading(false));
        invoiceRef.current?.showModal();
    };

    return (
        <form className={`alert`} onSubmit={handleSubmit}>
            <div className="flex justify-between items-center">
                <h2 className="text-gray-800 text-2xl font-bold">
                    Agendar cirugía
                </h2>
                <button
                    type="button"
                    className="hover:bg-black hover:bg-opacity-10 p-1 rounded transition-all"
                    onClick={closeDialog}
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>
            <div className="modal-input-grid mt-5">
                <SimpleCombobox
                    inputClassName="relative mt-1"
                    handleChange={handleChange}
                    onQueryChange={handleQueryChange}
                    placeholder={"Escribe el nombre..."}
                    label={"Paciente"}
                    name="patient"
                    className={`shadow-sm pl-2 mt-1 w-full bg-white border focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    options={patients}
                    required={true}
                    error={formErrors.patient}
                />
                <div>
                    <label
                        htmlFor="invoiceNumber"
                        className="block text-sm font-medium leading-6 text-gray-900"
                    >
                        Nro de Factura
                        <span className="font-medium text-[#F51515] ml-2">
                            *
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Escribe el nro. de factura"
                        name="invoiceNumber"
                        id="invoiceNumber"
                        onChange={(e) => {
                            setInvoiceNumber(e.target.value);
                            setFormErrors({
                                ...formErrors,
                                invoiceNumber: false,
                            });
                        }}
                        className={`shadow-sm pl-2 w-full bg-white border ${
                            formErrors.invoiceNumber
                                ? "border-red-500"
                                : "border-gray-300"
                        } focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    />
                    {formErrors.invoiceNumber && (
                        <p className="mt-2 text-sm text-red-600">
                            {formErrors.invoiceNumber}
                        </p>
                    )}
                </div>
                <div>
                    <label
                        htmlFor="date"
                        className="block text-sm font-medium leading-6 text-gray-900"
                    >
                        Fecha
                        <span className="font-medium text-[#F51515] ml-2">
                            *
                        </span>
                    </label>
                    <input
                        type="date"
                        name="date"
                        id="date"
                        onChange={(e) => handleChange("date", e.target.value)}
                        className={`shadow-sm pl-2 w-full bg-white border ${
                            formErrors.date
                                ? "border-red-500"
                                : "border-gray-300"
                        } focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    />
                    {formErrors.date && (
                        <p className="mt-2 text-sm text-red-600">
                            {formErrors.date}
                        </p>
                    )}
                </div>
                <SimpleCombobox
                    inputClassName="relative mt-1"
                    placeholder={"Seleccione al cirujano"}
                    handleChange={handleChange}
                    name="surgeon"
                    label="Cirujano"
                    className={`shadow-sm pl-2 mt-1 w-full bg-white border focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    options={medicals}
                    required={true}
                    error={formErrors.surgeon}
                />
                <SimpleCombobox
                    inputClassName="relative mt-1"
                    placeholder={"Seleccione al ayudante"}
                    handleChange={handleChange}
                    name="assistant"
                    label="Ayudante"
                    className={`shadow-sm pl-2 mt-1 w-full bg-white border focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    options={medicals}
                    // required={true}
                    error={formErrors.assistant}
                />
                <SimpleCombobox
                    inputClassName="relative mt-1"
                    placeholder={"Seleccione al anestesista"}
                    handleChange={handleChange}
                    name="anesthesiologist"
                    label="Anestesista"
                    className={`shadow-sm pl-2 mt-1 w-full bg-white border focus:ring-primary focus:border-primary sm:text-sm rounded-md block`}
                    options={medicals}
                    // required={true}
                    error={formErrors.anesthesiologist}
                />
            </div>
            <button
                type="button"
                onClick={checkInvoice}
                className="w-full mt-6 flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
            >
                Agregar
            </button>
            <dialog
                ref={invoiceRef}
                id="invoice-data"
                closedby="any"
                className="rounded-lg overflow-visible"
            >
                <InvoiceData
                    invoice={invoice}
                    invoiceLoading={invoiceLoading}
                    dialogRef={invoiceRef}
                />
            </dialog>
        </form>
    );
};

const InvoiceData = (props) => {
    const closeDialog = () => {
        props.dialogRef.current?.close();
    };
    return (
        <>
            <div className="p-6 lg:w-[446px] min-h-[440px]">
                {props.invoiceLoading ? (
                    <div className="flex h-full items-center justify-center ">
                        <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                    </div>
                ) : props.invoice ? (
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex flex-col items-center">
                            <div className="bg-[#FFBB3D40] p-2 rounded-full ">
                                <ExclamationTriangleIcon className="h-6 w-6 text-[#FFBB3D]" />
                            </div>
                            <h2 className="font-semibold">
                                Corfima que los datos sean correctos
                            </h2>
                        </div>
                        <div className="flex flex-col items-center">
                            <b className="text-gray-800">Paciente</b>
                            <span className="text-gray-600">
                                {props.invoice.name}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <b className="text-gray-800">Razón social</b>
                            <span className="text-gray-600">
                                {props.invoice.socialReason}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <b className="text-gray-800">Cirugía a realizar</b>
                            <span className="text-gray-600">
                                {props.invoice.serviceDescription}
                            </span>
                        </div>
                        <div className="w-full">
                            <button
                                type="submit"
                                className="w-full mt-6 flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                            >
                                Confirmar
                            </button>
                            <button
                                type="button"
                                onClick={closeDialog}
                                className="w-full mt-6 flex justify-center items-center px-4 py-2 border border-gray-500 shadow-sm text-sm font-semibold rounded-md text-gray-800 bg-white"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full justify-between items-center flex flex-col">
                        <div className="flex mt-20 flex-col gap-5 items-center">
                            <div className="bg-[#FFF1F0] p-2 rounded-full ">
                                <ExclamationCircleIcon className="h-20 w-20 text-[#FF4D4F]" />
                            </div>
                            <h2 className="font-semibold text-center text-3xl">
                                No se ha encontrado la factura
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={closeDialog}
                            className="w-full mt-6 flex justify-center items-center px-4 py-2 border border-gray-500 shadow-sm text-sm font-semibold rounded-md text-gray-800 bg-white"
                        >
                            Volver
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};
